import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  X, Share2, Globe, Lock, Copy, Check, Loader2, 
  Facebook, Link, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareStashModal({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    is_public: false,
    display_name: '',
    bio: '',
    share_url: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stash/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      logger.error('Failed to load settings:', error);
      toast.error(isEnglish ? 'Failed to load settings' : 'Asetusten lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/stash/settings`, {
        is_public: settings.is_public,
        display_name: settings.display_name,
        bio: settings.bio
      }, { withCredentials: true });
      
      setSettings(response.data);
      toast.success(isEnglish ? 'Settings saved!' : 'Asetukset tallennettu!');
    } catch (error) {
      logger.error('Failed to save settings:', error);
      toast.error(isEnglish ? 'Failed to save settings' : 'Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  const getFullShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}${settings.share_url}`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getFullShareUrl());
      setCopied(true);
      toast.success(isEnglish ? 'Link copied!' : 'Linkki kopioitu!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(isEnglish ? 'Failed to copy' : 'Kopiointi epäonnistui');
    }
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getFullShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const text = isEnglish 
      ? `Check out my yarn stash on Even Rounds! 🧶`
      : `Katso lankavarastoni Even Roundsissa! 🧶`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getFullShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnPinterest = () => {
    const description = isEnglish 
      ? `My yarn stash collection on Even Rounds`
      : `Lankavarastoni Even Roundsissa`;
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(getFullShareUrl())}&description=${encodeURIComponent(description)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Share2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold">
                {isEnglish ? 'Share Your Stash' : 'Jaa Varastosi'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isEnglish ? 'Show off your yarn collection' : 'Esittele lankakokoelmaasi'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-stone-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Public Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl bg-stone-50">
              <div className="flex items-center gap-3">
                {settings.is_public ? (
                  <Globe className="h-5 w-5 text-green-600" />
                ) : (
                  <Lock className="h-5 w-5 text-stone-400" />
                )}
                <div>
                  <p className="font-medium">
                    {isEnglish ? 'Public Stash' : 'Julkinen Varasto'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {settings.is_public 
                      ? (isEnglish ? 'Anyone can view your stash' : 'Kuka tahansa voi nähdä varastosi')
                      : (isEnglish ? 'Only you can see your stash' : 'Vain sinä näet varastosi')}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.is_public}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, is_public: checked }))}
              />
            </div>

            {/* Display Name */}
            <div>
              <Label className="mb-2 block">
                {isEnglish ? 'Display Name' : 'Näyttönimi'}
              </Label>
              <Input
                value={settings.display_name}
                onChange={(e) => setSettings(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder={isEnglish ? 'Your name for visitors' : 'Nimesi vierailijoille'}
                className="rounded-xl"
              />
            </div>

            {/* Bio */}
            <div>
              <Label className="mb-2 block">
                {isEnglish ? 'Bio' : 'Kuvaus'}
              </Label>
              <Textarea
                value={settings.bio}
                onChange={(e) => setSettings(prev => ({ ...prev, bio: e.target.value }))}
                placeholder={isEnglish 
                  ? 'Tell visitors about your crafting journey...' 
                  : 'Kerro vierailijoille käsityömatkastasi...'}
                className="rounded-xl min-h-[80px]"
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground text-right mt-1">
                {settings.bio?.length || 0}/200
              </p>
            </div>

            {/* Save Button */}
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="w-full rounded-full bg-primary text-primary-foreground"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                isEnglish ? 'Save Settings' : 'Tallenna Asetukset'
              )}
            </Button>

            {/* Share Section - Only show if public */}
            {settings.is_public && (
              <>
                <div className="border-t border-stone-100 pt-6">
                  <h3 className="font-heading font-semibold mb-4">
                    {isEnglish ? 'Share Link' : 'Jaa Linkki'}
                  </h3>
                  
                  {/* Copy Link */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={getFullShareUrl()}
                      readOnly
                      className="rounded-xl bg-stone-50 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={copyLink}
                      className="rounded-xl px-4"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Social Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={shareOnFacebook}
                      className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-[#1877F2] text-white hover:opacity-90 transition-opacity"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <span className="text-sm font-medium">Facebook</span>
                    </button>
                    
                    <button
                      onClick={shareOnTwitter}
                      className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-black text-white hover:opacity-90 transition-opacity"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className="text-sm font-medium">X</span>
                    </button>
                    
                    <button
                      onClick={shareOnPinterest}
                      className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-[#E60023] text-white hover:opacity-90 transition-opacity"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                      </svg>
                      <span className="text-sm font-medium">Pinterest</span>
                    </button>
                  </div>

                  {/* Preview Link */}
                  <Button
                    variant="ghost"
                    onClick={() => window.open(getFullShareUrl(), '_blank')}
                    className="w-full mt-4 rounded-xl"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {isEnglish ? 'Preview Public Page' : 'Esikatsele Julkista Sivua'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
